import { useMemo, useState } from "react"
import { Link, router } from "@inertiajs/react"
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  RotateCcw,
} from "lucide-react"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type { RehearsalDetail, SuiteSkillBucket, RehearsalRunRow } from "@/types/agent_testing"

export type Filter = {
  failureOnly: boolean
  search: string
}

type Props = {
  rehearsal: RehearsalDetail
  filter: Filter
}

// CSRF-aware POST helper — mirrors the one in pages/Testing/Index.tsx so the
// per-row rerun button can hit /skills/:id/test_runs/:run_id/replay without
// reaching back up through page-level state.
async function csrfPost(path: string): Promise<Response> {
  const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
  })
}

export function RehearsalResultsTable({ rehearsal, filter }: Props) {
  const [busyRunIds, setBusyRunIds] = useState<Set<number>>(new Set())

  const filteredBuckets = useMemo(
    () => rehearsal.runs_by_skill.map((b) => ({ ...b, runs: applyFilter(b.runs, filter) })),
    [rehearsal.runs_by_skill, filter]
  )

  const visible = filteredBuckets.filter((b) => b.runs.length > 0)

  // Replay a single run. After the POST returns, ask Inertia to refresh
  // latestRehearsal so the row's status flips to running; the cable
  // subscription on Testing/Index will stream subsequent updates.
  async function rerunRun(runId: number, skillId: number | null) {
    setBusyRunIds((prev) => new Set(prev).add(runId))
    try {
      const path = skillId == null
        ? `/testing/test_runs/${runId}/replay`
        : `/skills/${skillId}/test_runs/${runId}/replay`
      const res = await csrfPost(path)
      if (!res.ok) {
        console.error("replay failed:", res.statusText)
        return
      }
      router.reload({ only: ["latestRehearsal"] })
    } finally {
      setBusyRunIds((prev) => {
        const next = new Set(prev); next.delete(runId); return next
      })
    }
  }

  if (visible.length === 0) {
    return (
      <div className="rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
        No runs match the current filter.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {visible.map((bucket) => (
        <SkillBucket
          key={`${bucket.skill_key}-${bucket.skill_id ?? "_"}`}
          bucket={bucket}
          busyRunIds={busyRunIds}
          onRerun={rerunRun}
        />
      ))}
    </div>
  )
}

function SkillBucket({
  bucket, busyRunIds, onRerun,
}: {
  bucket: SuiteSkillBucket
  busyRunIds: Set<number>
  onRerun: (runId: number, skillId: number | null) => void
}) {
  const allPassing = bucket.passing_count === bucket.total_count
  // Failing buckets default-open; passing buckets default-closed.
  const [open, setOpen] = useState(!allPassing)
  const passRate = bucket.total_count > 0 ? (bucket.passing_count / bucket.total_count) * 100 : 0

  return (
    <div className="rounded-card border border-border bg-app-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left transition hover:bg-white/5"
        id={bucket.skill_key ? `skill-${bucket.skill_key}` : undefined}
      >
        {open ? <ChevronDown size={14} className="text-app-muted" /> : <ChevronRight size={14} className="text-app-muted" />}
        <span className="font-medium text-app-text">{bucket.skill_name}</span>
        <code className="rounded bg-white/5 px-1 text-[11px] text-app-muted">{bucket.skill_key}</code>

        <div className="ml-auto flex items-center gap-3">
          <div className="h-1.5 w-24 rounded-full bg-app-panel2 overflow-hidden">
            <div
              className={cn("h-full", allPassing ? "bg-status-success" : "bg-status-error")}
              style={{ width: `${passRate}%` }}
            />
          </div>
          <span className={cn("text-xs", allPassing ? "text-status-success" : "text-status-error")}>
            {bucket.passing_count}/{bucket.total_count} passing
          </span>
        </div>
      </button>

      {open && (
        <div className="divide-y divide-border border-t border-border">
          {bucket.runs.map((run) => (
            <RunRow
              key={run.id}
              run={run}
              skillId={bucket.skill_id}
              busy={busyRunIds.has(run.id)}
              onRerun={() => onRerun(run.id, bucket.skill_id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function RunRow({
  run, skillId, busy, onRerun,
}: {
  run: RehearsalRunRow
  skillId: number | null
  busy: boolean
  onRerun: () => void
}) {
  const isBad = run.status === "failed" || run.status === "errored"
  const canRerun = true

  // Click navigates to the matching test surface. Skill rows open the skill
  // Test Lab; integration rows deep-link into the integration bench with the
  // scenario selected.
  const href = skillId != null
    ? (isBad
        ? `/skills/${skillId}/test?fix_run_id=${run.id}`
        : `/skills/${skillId}/test`)
    : run.integration_path ?? null

  const className = cn(
    "grid w-full grid-cols-[24px_1fr_auto_auto_auto_auto] items-center gap-3 px-4 py-2.5 text-left text-sm transition focus:outline-none",
    href != null && "cursor-pointer hover:bg-white/5 focus:bg-white/5",
    href == null && "opacity-70",
    isBad && "border-l-2 border-l-status-error/50",
  )

  const body = (
    <>
      <StatusIcon status={run.status} />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-app-text">{run.scenario_name || run.scenario_key}</span>
          <ModeChip mode={run.mode} />
        </div>
        <code className="text-[10px] text-app-muted">{run.scenario_key}</code>
      </div>
      <span className={cn("text-[11px] uppercase tracking-wide", statusTone(run.status))}>{run.status}</span>
      <span className="text-[11px] text-app-muted">
        {run.duration_ms != null ? `${(run.duration_ms / 1000).toFixed(1)}s` : "—"}
      </span>
      <span className="text-[11px] text-app-muted">{fmtCompactRelative(run.finished_at)}</span>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (canRerun && !busy) onRerun()
        }}
        disabled={!canRerun || busy}
        title={busy ? "Rerun in flight…" : "Rerun this scenario"}
        className={cn(
          "rounded-control border border-border p-1 text-app-muted transition",
          (canRerun && !busy) && "hover:border-brand-red hover:text-app-text",
          (!canRerun || busy) && "opacity-50",
        )}
      >
        <RotateCcw size={14} className={cn(busy && "animate-spin")} />
      </button>
    </>
  )

  if (href == null) {
    return <div className={className}>{body}</div>
  }

  return (
    <Link href={href} className={className}>
      {body}
    </Link>
  )
}

function StatusIcon({ status }: { status: string }) {
  if (status === "passed")  return <CheckCircle2 size={16} className="text-status-success" />
  if (status === "failed")  return <XCircle size={16} className="text-status-error" />
  if (status === "errored") return <AlertCircle size={16} className="text-status-warning" />
  return <Clock size={16} className="text-app-muted" />
}

function ModeChip({ mode }: { mode: "isolation" | "integration" }) {
  const isInt = mode === "integration"
  return (
    <span className={cn(
      "shrink-0 rounded-control border px-1.5 py-0.5 text-[9px] uppercase tracking-wide",
      isInt
        ? "border-sky-500/30 bg-sky-500/10 text-sky-300"
        : "border-app-muted/30 bg-white/5 text-app-muted",
    )}>
      {isInt ? "int" : "iso"}
    </span>
  )
}

function statusTone(status: string): string {
  if (status === "passed")  return "text-status-success"
  if (status === "failed")  return "text-status-error"
  if (status === "errored") return "text-status-warning"
  return "text-app-muted"
}

function applyFilter(runs: RehearsalRunRow[], filter: Filter): RehearsalRunRow[] {
  const q = filter.search.trim().toLowerCase()
  return runs.filter((r) => {
    if (filter.failureOnly && r.status !== "failed" && r.status !== "errored") return false
    if (q && !r.scenario_name.toLowerCase().includes(q) && !r.scenario_key.toLowerCase().includes(q)) return false
    return true
  })
}
